import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Copy, FileText, X } from "lucide-react";
import { useStoryMode } from "../../hooks/useStoryMode";
import { useActiveSection } from "../../hooks/useActiveSection";
import { onFlow } from "../../lib/sectionFlow";
import { EMAIL, SOCIAL_LINKS } from "../../data/contact";
import { createDockLink, type DockNudge } from "./badgeLink";
import "./BadgeDock.css";

const Lanyard = lazy(() => import("./Lanyard"));

/** The pre-scaled photo: the card's photo window is ~600px, the original 2944px. */
const PHOTO = "/IMG_3159-card.jpg";
const GITHUB = SOCIAL_LINKS.find((s) => s.name === "GitHub")?.url ?? "https://github.com";
const IDS = ["home", "about", "experience", "projects", "contact"];

/*
 * Where the card sits inside the lanyard's frame, as fractions of the frame's
 * height. Fixed by the scene's own framing (Lanyard.tsx: fov 20 at z 30, the
 * anchor at y 6.5, three 2.23 rope segments, CARD_SCALE 3.05): the card hangs
 * with its top 62% of the way down the frame and is 29% of it tall.
 */
const CARD_TOP = 0.6226;
const CARD_H = 0.2906;
/** The card's width ÷ height (64.5 × 90 mm). */
const CARD_ASPECT = 0.7183;
/** Below this width there's no room right of the menu, and the badge stays away. */
const MIN_CARD_W = 42;
/** How long the card keeps animating after something moved it (ms). */
const AWAKE_MS = 3200;

interface Geo {
    /** The lanyard's frame box (fixed, viewport px). */
    left: number;
    top: number;
    width: number;
    height: number;
    /** The card itself. */
    cx: number;
    cardTop: number;
    cardW: number;
    cardH: number;
}

function frameFor(cx: number, cardTop: number, cardH: number): Geo {
    const height = cardH / CARD_H;
    const width = height * 0.62;
    return { left: cx - width / 2, top: cardTop - CARD_TOP * height, width, height, cx, cardTop, cardW: cardH * CARD_ASPECT, cardH };
}

/**
 * Parked: in front of the navbar, in the empty corner right of the menu,
 * sized to that gap. Open: dropped lower and larger, toward the right edge.
 */
function measure(open: boolean): Geo | null {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (open) {
        const cardH = Math.min(380, vh * 0.48);
        return frameFor(vw - Math.max(230, cardH * 0.36 + 110), 92, cardH);
    }
    const nav = document.querySelector<HTMLElement>(".navbar-container");
    if (!nav) return null;
    const r = nav.getBoundingClientRect();
    const navRight = r.right - (parseFloat(getComputedStyle(nav).paddingRight) || 24);
    const free = vw - navRight;
    const cardW = Math.min(68, free - 14);
    if (cardW < MIN_CARD_W) return null;
    return frameFor(navRight + Math.min(free, 150) / 2, 10, cardW / CARD_ASPECT);
}

/**
 * The companion badge: once the reader leaves Index, the lanyard card follows
 * them, hanging small in front of the navbar's right-hand corner. It answers
 * each section transition (a turn, a swing, a flip), and a click drops it
 * down, turned to its contact side, with real buttons beneath it.
 *
 * Desktop story mode only; phones have no corner to spare. A second lanyard
 * scene, separate from the hero's: only one of the two renders at a time.
 */
export default function BadgeDock() {
    const story = useStoryMode();
    const { pathname } = useLocation();
    const active = useActiveSection(IDS, 80);
    const [link] = useState(createDockLink);
    const [open, setOpen] = useState(false);
    const [hover, setHover] = useState(false);
    const [copied, setCopied] = useState(false);
    const [, setTick] = useState(0);

    // Lanyard's own gate: a real pointer, so not a touch screen at any size.
    const [finePointer, setFinePointer] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
        setFinePointer(mq.matches);
        const onChange = (e: MediaQueryListEvent) => setFinePointer(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    // Re-measure the corner when the window changes size.
    useEffect(() => {
        const onResize = () => setTick((t) => t + 1);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const parked = story && finePointer ? measure(false) : null;
    const wanted = !!parked && pathname === "/" && active !== "home";

    /*
     * Shown → in place, in two steps: the frame is laid out above the viewport
     * first, then moved down on the next frame so the CSS transition carries
     * the card in. Leaving runs the same move upward before hiding.
     */
    const [shown, setShown] = useState(false);
    const [placed, setPlaced] = useState(false);
    const [mounted, setMounted] = useState(false);
    /*
     * Build the docked scene ahead of time, hidden, once the page has been
     * quiet for a few seconds: a second WebGL context compiles its shaders
     * from scratch, and doing that the moment the reader first leaves Index
     * made the card arrive late and the transition stutter.
     */
    const canDock = !!parked && pathname === "/";
    useEffect(() => {
        if (!canDock || mounted) return;
        let idle = 0;
        const t = window.setTimeout(() => {
            if ("requestIdleCallback" in window) idle = window.requestIdleCallback(() => setMounted(true), { timeout: 3000 });
            else setMounted(true);
        }, 4000);
        return () => {
            window.clearTimeout(t);
            if (idle && "cancelIdleCallback" in window) window.cancelIdleCallback(idle);
        };
    }, [canDock, mounted]);
    useEffect(() => {
        if (wanted) {
            setMounted(true);
            setShown(true);
            const id = requestAnimationFrame(() => requestAnimationFrame(() => setPlaced(true)));
            return () => cancelAnimationFrame(id);
        }
        setPlaced(false);
        setOpen(false);
        const id = window.setTimeout(() => setShown(false), 650);
        return () => window.clearTimeout(id);
    }, [wanted]);

    // The render loop runs only while something is moving the card.
    const [awake, setAwake] = useState(false);
    const sleepTimer = useRef(0);
    const wake = useCallback(
        (ms = AWAKE_MS) => {
            setAwake(true);
            window.clearTimeout(sleepTimer.current);
            // Sleep only once the card has actually come to rest, so it never
            // freezes mid-swing.
            const trySleep = () => {
                if (link.settled && !link.queue.length) setAwake(false);
                else sleepTimer.current = window.setTimeout(trySleep, 400);
            };
            sleepTimer.current = window.setTimeout(trySleep, ms);
        },
        [link]
    );
    useEffect(() => () => window.clearTimeout(sleepTimer.current), []);

    const nudge = useCallback(
        (kind: DockNudge, dir: number) => {
            link.queue.push({ kind, dir });
            wake();
        },
        [link, wake]
    );

    useEffect(() => {
        if (placed) nudge("drop", 1);
        else if (shown) wake(900);
    }, [placed, shown, nudge, wake]);

    // Each section transition: close the card if it's open, and let it react.
    useEffect(
        () =>
            onFlow((mode, dir, to) => {
                setOpen(false);
                if (to !== "home") nudge(mode, dir);
            }),
        [nudge]
    );

    useEffect(() => {
        link.onToggle = () => setOpen((o) => !o);
        link.onHover = (h) => {
            setHover(h);
            if (h) wake();
        };
        return () => {
            link.onToggle = undefined;
            link.onHover = undefined;
        };
    }, [link, wake]);

    useEffect(() => {
        link.open = open;
        if (!open) link.settleUntil = performance.now() + 900;
        wake(open ? 4000 : 2400);
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, link, wake]);

    const copy = () => {
        navigator.clipboard?.writeText(EMAIL).then(
            () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
            },
            () => undefined
        );
    };

    if (!parked && !shown) return null;
    const rest = parked ?? measure(false);
    if (!rest) return null;
    const g = open ? measure(true)! : rest;
    // Out of sight above the viewport, ready to drop in.
    const above = { ...rest, top: rest.top - (rest.cardTop + rest.cardH + 80) };
    const frame = placed ? g : above;
    /*
     * The canvas strip runs from a little left of wherever the card can be
     * (parked or open) to the right edge. On a very wide screen the card parks
     * far from that edge — the navbar is capped at 1200px — and a fixed-width
     * strip used to cut it in half.
     */
    const open3 = measure(true);
    const stripLeft = Math.max(0, Math.min(rest.cx, open3 ? open3.cx : rest.cx) - 320);

    return (
        <>
            {open && <div className="badge-scrim" onClick={() => setOpen(false)} />}
            <div
                className={`badge-dock${open ? " is-open" : ""}`}
                style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height }}
                aria-hidden="true"
            >
                {mounted && (
                    <Suspense fallback={null}>
                        <Lanyard role="dock" dock={link} active={shown} awake={awake} stripLeft={stripLeft} frontImage={PHOTO} />
                    </Suspense>
                )}
            </div>

            {/* Keyboard access to the card: invisible until focused, then a
                ring around it. Pointer clicks go to the 3D card itself. */}
            {wanted && (
                <button
                    type="button"
                    className="badge-dock-key"
                    style={{ left: rest.cx - rest.cardW / 2, top: rest.cardTop, width: rest.cardW, height: rest.cardH }}
                    aria-expanded={open}
                    aria-controls="badge-dock-panel"
                    aria-label={open ? "Close the contact card" : "Open the contact card"}
                    onClick={() => setOpen((o) => !o)}
                />
            )}

            <span
                className={`badge-dock-tip mono${hover && !open && placed ? " is-on" : ""}`}
                // Kept clear of the screen edge on narrow windows.
                style={{ left: Math.min(rest.cx, window.innerWidth - 66), top: rest.cardTop + rest.cardH + 10 }}
                aria-hidden="true"
            >
                Quick contact
            </span>

            {open && placed && (
                <div
                    id="badge-dock-panel"
                    className="badge-dock-panel"
                    style={{ left: g.cx, top: g.cardTop + g.cardH + 18 }}
                    role="dialog"
                    aria-label="Contact card"
                >
                    <button type="button" className="badge-dock-action" onClick={copy}>
                        <Copy size={14} strokeWidth={2} />
                        {copied ? "Copied" : "Copy email"}
                    </button>
                    <Link to="/resume" className="badge-dock-action" onClick={() => setOpen(false)}>
                        <FileText size={14} strokeWidth={2} />
                        Résumé
                    </Link>
                    <a href={GITHUB} target="_blank" rel="noopener noreferrer" className="badge-dock-action">
                        GitHub
                        <ArrowUpRight size={14} strokeWidth={2} />
                    </a>
                    <button type="button" className="badge-dock-close" onClick={() => setOpen(false)} aria-label="Close">
                        <X size={15} strokeWidth={2} />
                    </button>
                </div>
            )}
        </>
    );
}
